import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageview, trackEvent } from '../utils/analytics.js';

/**
 * Hook personnalisé pour suivre automatiquement les vues de pages et les changements d'onglets
 *
 * @param {Object} options - Options de configuration
 * @param {string} options.cockpitName - Nom du cockpit actuel (pour le suivi des onglets)
 * @param {string} options.tabName - Nom de l'onglet actuel
 * @param {string} options.tabSlug - Slug de l'onglet actuel
 * @param {Object} options.currentUser - L'utilisateur connecté (facultatif)
 */
export default function useAnalytics(options = {}) {
  const location = useLocation();
  const { pathname, hash } = location;
  const { cockpitName, tabName, tabSlug, currentUser } = options;

  // Référence pour garder trace du dernier onglet
  const lastTabRef = useRef(null);

  // Suivi des changements de pages (sans le hash)
  useEffect(() => {
    let props = {};

    // Ajouter les infos d'utilisateur si disponibles
    if (currentUser) {
      props = {
        ...props,
        user: currentUser.username,
        client: currentUser.client,
        isAdmin: currentUser.isadmin || currentUser.issuperadmin
      };
    }

    // Effectuer le tracking de la page vue
    trackPageview({
      url: pathname,
    }, { props });
  }, [pathname, currentUser]);

  // Suivi des changements d'onglets (trackEvent)
  useEffect(() => {
    // Ne déclencher que si on a les infos nécessaires et si l'onglet a changé
    if (cockpitName && tabName && (tabName !== lastTabRef.current)) {
      lastTabRef.current = tabName;

      let props = {
        cockpit: cockpitName,
        tab: tabName,
        tabSlug: tabSlug || ''
      };

      // Ajouter les infos d'utilisateur si disponibles
      if (currentUser) {
        props = {
          ...props,
          user: currentUser.username,
          client: currentUser.client,
          isAdmin: currentUser.isadmin || currentUser.issuperadmin
        };
      }

      // Suivre le changement d'onglet comme un événement
      trackEvent('viewTab', { props });
    }
  }, [cockpitName, tabName, tabSlug, hash, currentUser]);

  // Pas besoin de retourner quoi que ce soit, le hook s'occupe du tracking en arrière-plan
  return null;
}